
/**
 * Enhanced error tracking utility
 * Provides improved error tracking with minimal performance impact
 */

// Import basic tracker as fallback
import { trackError, ErrorSeverity } from './errorTracker';

// Error rate limiting with Map for better performance
const errorRateLimiter = new Map<string, { count: number, timestamp: number }>();
const ERROR_RATE_WINDOW = 120000; // 2 minutes (in milliseconds)
const DEFAULT_RATE_LIMIT = 3; // Default limit per window

/**
 * Track error with enhanced context and rate limiting
 * More performance-optimized version of trackError
 * 
 * @param error Error object or message
 * @param component Component where error occurred
 * @param additionalInfo Additional context for the error
 * @param severity Error severity level (defaults to ERROR)
 * @param rateLimit Maximum times to log same error in rate window (default: 3)
 */
export function trackErrorEnhanced(
  error: Error | string | any,
  component: string = 'Unspecified',
  additionalInfo: Record<string, any> = {},
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  rateLimit: number = DEFAULT_RATE_LIMIT
): void {
  try {
    // Skip tracking completely in production if not a critical error
    if (process.env.NODE_ENV === 'production' && severity !== ErrorSeverity.CRITICAL) {
      return;
    }
    
    // Create error message
    const errorMessage = error instanceof Error 
      ? error.message 
      : (typeof error === 'string' ? error : JSON.stringify(error));
    
    // Create simple hash for rate limiting
    const errorKey = `${component}:${errorMessage.substring(0, 50)}`;
    
    // Check rate limit - only apply in production
    if (process.env.NODE_ENV === 'production') {
      const now = Date.now();
      const errorRate = errorRateLimiter.get(errorKey);
      
      if (errorRate) {
        // If within rate window, increment count
        if (now - errorRate.timestamp < ERROR_RATE_WINDOW) {
          if (errorRate.count >= rateLimit) {
            // Skip tracking if over rate limit
            return;
          }
          errorRate.count += 1;
        } else {
          // Reset if outside window
          errorRate.count = 1;
          errorRate.timestamp = now;
        }
      } else {
        // First occurrence
        errorRateLimiter.set(errorKey, { count: 1, timestamp: now });
      }
      
      // Clean up rate limiter occasionally
      if (errorRateLimiter.size > 50) {
        const keysToDelete: string[] = [];
        errorRateLimiter.forEach((value, key) => {
          if (now - value.timestamp > ERROR_RATE_WINDOW) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach(key => errorRateLimiter.delete(key));
      }
    }
    
    // Add timestamp to additional info
    const enhancedInfo = {
      ...additionalInfo,
      timestamp: new Date().toISOString(),
      environmentMode: process.env.NODE_ENV || 'development'
    };
    
    // Use basic tracker as implementation
    trackError(error, component, enhancedInfo, severity);
    
  } catch (e) {
    // Never let error tracking cause more errors
    console.error('Error in enhanced error tracking:', e);
    
    // Fallback to basic console logging
    try {
      console.error(`[${component}] Error: ${error instanceof Error ? error.message : error}`);
    } catch {
      // Ultimate fallback - silent fail
    }
  }
}

export default {
  trackErrorEnhanced
};
